/**
 * Resolves which image represents the speaker of an event.
 *
 * The site had two disconnected answers to this question. Event cards read the
 * published speaker profile for that exact event slug and otherwise fell back to
 * the event's own hero image; the speaker filter read a hard-coded map of bundled
 * portrait files keyed by name. So a speaker whose profile was not yet published
 * and whose event carried no hero image — Nahed, Farrah, Diane — had a face in
 * the filter dropdown and a blank circle on their own card, which reads as a
 * loading bug rather than as missing data.
 *
 * One resolver now answers for both surfaces, in order of authority: what an
 * admin published for this event, what the same person published elsewhere, the
 * the shared generic portrait for group events, then the portrait the site
 * already ships, then the event's own artwork.
 */

import {
  BUNDLED_SPEAKER_PORTRAITS,
  bundledSpeakerPortraitForName,
  isMultipleSpeakerName,
  MULTIPLE_SPEAKERS_PORTRAIT,
  speakerNameKey,
} from "@shared/speakerPortraitCatalog";

export {
  BUNDLED_SPEAKER_PORTRAITS,
  isMultipleSpeakerName,
  MULTIPLE_SPEAKERS_PORTRAIT,
} from "@shared/speakerPortraitCatalog";

/** The published speaker fields this resolver needs; the roster carries more. */
export type PublishedSpeakerPortrait = {
  eventSlug: string;
  name: string;
  portraitUrl?: string | null;
};

export function resolveSpeakerPortrait({
  eventSlug,
  speakerName,
  heroImage,
  publishedSpeakers,
}: {
  eventSlug: string;
  speakerName: string;
  heroImage?: string | null;
  publishedSpeakers: readonly PublishedSpeakerPortrait[];
}): string | null {
  /*
   * A group is not a person, so neither an individual portrait nor an event
   * hero should be shown in a circle labelled for multiple speakers.
   */
  if (isMultipleSpeakerName(speakerName)) return MULTIPLE_SPEAKERS_PORTRAIT;

  const withPortrait = publishedSpeakers.filter(speaker => speaker.portraitUrl);

  const forThisEvent = withPortrait.find(speaker => speaker.eventSlug === eventSlug);
  if (forThisEvent?.portraitUrl) return forThisEvent.portraitUrl;

  // The same person speaking at a second event is still the same face, and their
  // portrait is published once rather than per booking.
  const key = speakerNameKey(speakerName);
  const samePerson = withPortrait.find(speaker => speakerNameKey(speaker.name) === key);
  if (samePerson?.portraitUrl) return samePerson.portraitUrl;

  return bundledSpeakerPortraitForName(speakerName) ?? heroImage ?? null;
}
